import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlay, FiPause, FiSquare, FiCoffee } from 'react-icons/fi';

const Timer = () => {
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState('focus'); // focus, break

  const totalTime = mode === 'focus' ? 25 * 60 : 5 * 60;
  const progress = ((totalTime - timeLeft) / totalTime) * 100;

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      // Auto switch modes could go here
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft]);

  const toggleTimer = () => setIsActive(!isActive);
  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(totalTime);
  };
  
  const switchMode = (newMode) => {
    setMode(newMode);
    setIsActive(false);
    setTimeLeft(newMode === 'focus' ? 25 * 60 : 5 * 60);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center min-h-[80vh]"
    >
      <div className="glass-panel p-8 md:p-12 rounded-[3rem] w-full max-w-md flex flex-col items-center relative overflow-hidden">
        
        {/* Glow effect when active */}
        {isActive && (
          <div className="absolute inset-0 bg-[#eaff00]/5 animate-pulse rounded-[3rem]"></div>
        )}

        <div className="flex space-x-4 mb-10 relative z-10">
          <button 
            onClick={() => switchMode('focus')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${mode === 'focus' ? 'bg-[#eaff00] text-[#0b0c1b]' : 'bg-[#15172b] text-slate-400 hover:text-slate-200'}`}
          >
            Deep Focus
          </button>
          <button 
            onClick={() => switchMode('break')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center ${mode === 'break' ? 'bg-blue-400 text-[#0b0c1b]' : 'bg-[#15172b] text-slate-400 hover:text-slate-200'}`}
          >
            <SafeIcon icon={FiCoffee} className="mr-2" /> Short Break
          </button>
        </div>

        <div className="relative mb-12 z-10 w-64 h-64 flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full transform -rotate-90">
            <circle cx="128" cy="128" r="120" stroke="rgba(42, 45, 79, 0.5)" strokeWidth="8" fill="none" />
            <circle 
              cx="128" cy="128" r="120" 
              stroke={mode === 'focus' ? '#eaff00' : '#60a5fa'} 
              strokeWidth="8" fill="none" 
              strokeDasharray={120 * 2 * Math.PI} 
              strokeDashoffset={(120 * 2 * Math.PI) - (progress / 100) * (120 * 2 * Math.PI)}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-linear"
              style={{ filter: `drop-shadow(0 0 8px ${mode === 'focus' ? '#eaff0080' : '#60a5fa80'})` }}
            />
          </svg>
          <div className="flex flex-col items-center">
            <span className={`text-6xl font-bold tracking-tighter ${mode === 'focus' ? 'text-[#eaff00]' : 'text-blue-400'}`}>
              {formatTime(timeLeft)}
            </span>
            <span className="text-slate-400 mt-2 tracking-widest uppercase text-xs">
              {mode === 'focus' ? 'Session in progress' : 'Recharging'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-6 z-10">
          <button 
            onClick={toggleTimer}
            className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl transition-transform hover:scale-105 ${mode === 'focus' ? 'bg-[#eaff00] text-[#0b0c1b] glow-yellow' : 'bg-blue-400 text-[#0b0c1b] shadow-[0_0_15px_rgba(96,165,250,0.4)]'}`}
          >
            <SafeIcon icon={isActive ? FiPause : FiPlay} className={isActive ? '' : 'ml-1'} />
          </button>
          
          <button 
            onClick={resetTimer}
            className="w-12 h-12 rounded-full bg-[#2a2d4f] flex items-center justify-center text-slate-300 hover:bg-[#3a3d6f] transition-colors"
          >
            <SafeIcon icon={FiSquare} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default Timer;